import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BASE_URL from "../../config";
import { fetchWithAuth } from "../../utils/authUtils";
import { readErrorResponse } from "../../utils/readErrorResponse";

export const postPayment = createAsyncThunk(
    'payment/postPayment',
    async (orderData, { rejectWithValue }) => {

        console.log('Payment request data:', JSON.stringify(orderData));
        
        try {
            const response = await fetchWithAuth(`${BASE_URL}/payments/v1/transactions/create/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData),
            });

            console.log('Payment response status:', response.status);
            console.log('Payment response headers:', response.headers);

            if (!response.ok) {
                const errorDetails = await readErrorResponse(response);
                console.log('Payment error details:', errorDetails);
                
                return rejectWithValue(errorDetails);
            }

            const res = await response.json();
            console.log('Payment success response:', res);
            return res
        } catch (error) {
            console.error('Payment request failed:', error);
            return rejectWithValue({
                error: error.message || 'Network error occurred'
            });
        }
    }
);

export const prepareInlineCheckout = createAsyncThunk(
    'payment/prepareInlineCheckout',
    async (orderData, { rejectWithValue }) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/payments/v1/inline-checkout/prepare/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData),
            });

            if (!response.ok) {
                const errorDetails = await readErrorResponse(response);
                return rejectWithValue(errorDetails);
            }

            const res = await response.json();
            return res
        } catch (error) {
            return rejectWithValue({
                error: error.message || 'Network error occurred'
            });
        }
    }
);

export const confirmPayment = createAsyncThunk(
    'payment/confirmPayment',
    async (orderId, { rejectWithValue }) => {
        console.log('Confirming payment for order:', orderId);
        try {
            const response = await fetchWithAuth(`${BASE_URL}/payments/v1/confirm-payment/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ order_id: orderId }),
            });

            console.log('Confirm payment response status:', response.status);

            if (!response.ok) {
                const errorDetails = await readErrorResponse(response);
                console.log('Confirm payment error:', errorDetails);
                return rejectWithValue(errorDetails);
            }

            const res = await response.json();
            console.log('Payment confirmed:', res);
            return res;
        } catch (error) {
            console.error('Confirm payment failed:', error);
            return rejectWithValue({
                error: error.message || 'Confirm payment failed'
            });
        }
    }
);

const initialState={
    status:'idle',
    error: null,
    paymentData: null,
    inlineCheckoutData: null,
    inlineCheckoutStatus: 'idle'
}

const paymentSlice = createSlice({
    name: "payment",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetStatus: (state) => {
            state.status = 'idle';
            state.error = null;
        },
        clearInlineCheckoutData: (state) => {
            state.inlineCheckoutData = null;
            state.inlineCheckoutStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(postPayment.pending, (state) => {
                state.status = 'loading';
                state.error = null; 
            })
            .addCase(postPayment.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.paymentData = action.payload; 
                state.error = null; 
            })
            .addCase(postPayment.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload?.error || action.error?.message || 'Payment failed';
            })
            .addCase(prepareInlineCheckout.pending, (state) => {
                state.inlineCheckoutStatus = 'loading';
                state.error = null;
            })
            .addCase(prepareInlineCheckout.fulfilled, (state, action) => {
                state.inlineCheckoutStatus = 'succeeded';
                state.inlineCheckoutData = action.payload;
                state.error = null;
            })
            .addCase(prepareInlineCheckout.rejected, (state, action) => {
                state.inlineCheckoutStatus = 'failed';
                state.error = action.payload?.error || action.error?.message || 'Inline checkout transaction creation failed';
            });
    }
});

export const { clearError, resetStatus, clearInlineCheckoutData } = paymentSlice.actions;
export default paymentSlice.reducer;
